import { clear_organization_session_details, getCookie } from "@/router";
import { client_domain } from "@/main.js";

export function is_enabled_feature(value) {
    return value === true || value === 'true' || value === 1 || value === '1';
}

const REQUIRED_PROFILE_FIELDS = [
    ['race', 'race_id'],
    ['gender', 'gender_id'],
    ['age', 'date_of_birth'],
    ['location'],
];

const REQUIRED_PROFILE_CHECKS = REQUIRED_PROFILE_FIELDS.map((field_names) =>
    (user_details, context) => context.has_missing_profile_field(user_details, field_names)
);

function required_name_check(user_details, context) {
    return context.has_missing_names(user_details);
}

const ORGANIZATION_DETAIL_FIELDS = [
    ['organization_name', (company) => get_value(company, 'company_name')],
    ['company_name', (company) => get_value(company, 'company_name')],
    ['type_of_organization', (company, context) => context.get_organization_type_value(get_value(company, 'type_of_organization'))],
    ['organization_description', (company) => get_value(company, 'description')],
    ['organization_email', (company) => get_value(company, 'email')],
    ['organization_tell_number', (company) => get_value(company, 'tell_number')],
    ['organization_location', (company) => first_present_value([get_value(company, 'location'), get_value(company, 'address')])],
    ['organization_id', (company, context) => context.get_company_id_from_response(company)],
    ['is_private', (company) => get_value(company, 'is_private') === true],
    ['logo', (company) => first_present_value([get_value(company, 'logo'), get_value(get_value(company, 'base_profile'), 'logo')])],
    ['cover', (company) => first_present_value([get_value(company, 'cover'), get_value(company, 'cover_picture'), get_value(get_value(company, 'base_profile'), 'cover')])],
];

function get_value(source, key) {
    return source ? Reflect.get(source, key) : undefined;
}

function first_present_value(values = []) {
    return values.find(Boolean) || '';
}

function map_organization_detail_field(company, context, field) {
    const [key, get_value_from_field] = field;
    return [key, get_value_from_field(company, context) || ''];
}

export const authFinaleSharedMethods = {
    async ensure_csrf_cookie() {
        const csrfUrl = `${process.env.VUE_APP_BACKEND_API_URL}/system_management/get_csrf_token/`;
        try {
            await fetch(csrfUrl, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Page-URL': window.location.href,
                },
            });
        } catch (e) {
            console.warn('Failed to refresh CSRF cookie', e);
        } finally {
            this.csrfToken = getCookie('csrftoken') || this.csrfToken;
        }
    },

    is_show_organisation_enabled() {
        return is_enabled_feature(this.client_features.show_organisation);
    },

    has_dynamic_forms_feature() {
        return is_enabled_feature(this.client_features.show_dynamic_forms);
    },

    get_client_domain_value() {
        return String(client_domain
            || document.documentElement.getAttribute('data-client')
            || window.location.hostname
            || '').toLowerCase();
    },

    is_bosa_domain() {
        return this.get_client_domain_value().includes('bosa');
    },

    get_organization_type_value(type_of_organization) {
        if (!type_of_organization) return '';
        if (typeof type_of_organization === 'string') return type_of_organization;
        return get_value(type_of_organization, 'type_of_organization') || '';
    },

    get_company_id_from_response(company) {
        return get_value(company, 'id');
    },

    get_user_details_payload(response_data) {
        const user_details = get_value(response_data, 'user_details') || {};

        return this.client_features?.show_organisation
            ? Object.assign({}, user_details, this.get_organization_details_payload(response_data))
            : user_details;
    },

    get_organization_details_payload(response_data) {
        const company = this.get_company_from_response(response_data);
        const entries = ORGANIZATION_DETAIL_FIELDS.map((field) =>
            map_organization_detail_field(company, this, field)
        );

        return Object.fromEntries(entries);
    },

    set_active_organization_profile(response_data) {
        if (!this.is_show_organisation_enabled()) {
            clear_organization_session_details();
            return;
        }

        const company = this.get_company_from_response(response_data);
        this.store_active_organization_profile(company);
    },

    store_active_organization_profile(company) {
        const organization_id = this.get_company_id_from_response(company);
        if (!organization_id) return;

        localStorage.setItem('organization_id', organization_id);
        localStorage.setItem('active_profile', JSON.stringify(this.get_active_organization_profile(company, organization_id)));
        localStorage.setItem('active_profile_type', 'business');
    },

    get_active_organization_profile(company, organization_id) {
        const base_profile = get_value(company, 'base_profile') || {};

        return {
            ...base_profile,
            company,
            company_id: organization_id,
            profile_name: this.get_company_profile_name(company),
            profile_type: get_value(base_profile, 'profile_type') || this.get_default_business_profile_type(),
            is_active: true,
        };
    },

    get_company_profile_name(company) {
        const base_profile = get_value(company, 'base_profile');

        return first_present_value([
            get_value(base_profile, 'profile_name'),
            get_value(company, 'company_name'),
            get_value(company, 'organization_name'),
        ]);
    },

    get_default_business_profile_type() {
        return {
            name: 'business',
            profile_type: 'business',
        };
    },

    get_company_from_response(response_data) {
        const organizations = get_value(response_data, 'organizations');

        return Array.isArray(organizations)
            ? this.get_company_from_organizations(organizations)
            : this.get_company_from_single_response(response_data, organizations);
    },

    get_company_from_single_response(response_data, organizations) {
        return first_present_value([
            get_value(response_data, 'company'),
            get_value(response_data, 'organization'),
            organizations,
            {},
        ]);
    },

    get_company_from_organizations(organizations = []) {
        const stored_organization_id = localStorage.getItem('organization_id');
        return first_present_value([
            organizations.find((organization) => this.matches_stored_organization(organization, stored_organization_id)),
            organizations.find((organization) => this.has_organization_identifier(organization)),
            organizations[0],
            {},
        ]);
    },

    matches_stored_organization(organization, stored_organization_id) {
        return Boolean(stored_organization_id)
            && String(this.get_organization_identifier(organization)) === String(stored_organization_id);
    },

    has_organization_identifier(organization) {
        return Boolean(this.get_organization_identifier(organization));
    },

    get_organization_identifier(organization) {
        return first_present_value([
            get_value(organization, 'id'),
            get_value(organization, 'company_id'),
        ]);
    },

    has_missing_names(user_details) {
        return !String(get_value(user_details, 'first_name') || '').trim()
            || !String(get_value(user_details, 'last_name') || '').trim();
    },

    has_missing_required_profile_details(user_details, options = {}) {
        return this.get_required_profile_checks(options)
            .some((check) => check(user_details, this));
    },

    get_required_profile_checks(options = {}) {
        return options.include_names === false
            ? REQUIRED_PROFILE_CHECKS
            : [required_name_check, ...REQUIRED_PROFILE_CHECKS];
    },

    has_missing_profile_field(user_details, field_names) {
        const profile = get_value(user_details, 'profile') || {};
        return !field_names.some((field_name) => this.has_profile_value(this.get_allowed_profile_field_value(user_details, field_name))
            || this.has_profile_value(this.get_allowed_profile_field_value(profile, field_name)));
    },

    get_allowed_profile_field_value(source, field_name) {
        if (!source) return undefined;

        const allowed_profile_fields = [
            ['race', get_value(source, 'race')],
            ['race_id', get_value(source, 'race_id')],
            ['gender', get_value(source, 'gender')],
            ['gender_id', get_value(source, 'gender_id')],
            ['age', get_value(source, 'age')],
            ['date_of_birth', get_value(source, 'date_of_birth')],
            ['location', get_value(source, 'location')],
        ];
        const matched_field = allowed_profile_fields.find(([allowed_field_name]) => allowed_field_name === field_name);
        return matched_field ? matched_field[1] : undefined;
    },

    has_profile_value(value) {
        if (value === null || value === undefined) return false;
        return typeof value === 'string' ? value.trim() !== '' : true;
    },

    set_profile_modal_session(user_details) {
        const user_id = first_present_value([
            get_value(user_details, 'id'),
            get_value(user_details, 'user_id'),
        ]);
        sessionStorage.setItem('open_profile_modal', 'true');
        if (user_id) {
            sessionStorage.setItem('profile_target_user_id', user_id);
        } else {
            sessionStorage.removeItem('profile_target_user_id');
        }
    },

    clear_profile_modal_session() {
        sessionStorage.removeItem('open_profile_modal');
        sessionStorage.removeItem('profile_target_user_id');
        sessionStorage.removeItem('profile_target_user_name');
    },
};
